import React, { useState } from "react";
import { useParams } from "react-router-dom";
import Snackbar from "../UI/Snackbar";
import { getApiUrl } from "../../config/api";
import { useSnackbar } from "../../shared/hooks/useSnackbar";

const SubCreateCategory: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();

  const [formData, setFormData] = useState({
    categoryName: "",
    slug: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const {
    showSnackbar,
    snackbarType,
    apiMessage,
    setShowSnackbar,
    showMessage,
  } = useSnackbar();

  const handleInputChange = (field: string, value: string | File | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.categoryName.trim() || !formData.slug.trim()) {
      setError("Sub Category Name and Slug are required.");
      showMessage("Sub Category Name and Slug are required.", "error");
      return;
    }

    if (!categoryId) {
      setError("Category ID is missing from URL parameters.");
      showMessage("Category ID is missing from URL parameters.", "error");
      return;
    }

    setIsLoading(true);

    try {
      const requestBody = {
        name: formData.categoryName,
        slug: formData.slug,
        category: categoryId,
      };

      const response = await fetch(
        getApiUrl("/api/templates/v1.0.0/templates-subcategory"),
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();
      console.log("Sub Category created successfully:", result);

      // Reset form
      setFormData({
        categoryName: "",
        slug: "",
      });

      // Show success snackbar
      showMessage("Sub Category added successfully!", "success");
    } catch (err) {
      console.error("Error creating sub category:", err);

      setError(
        err instanceof Error
          ? err.message
          : "Failed to create sub category. Please try again."
      );
      showMessage(
        err instanceof Error
          ? err.message
          : "Failed to create sub category. Please try again.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto my-10">
      <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-gray-800 mb-2">
            Create Sub Category
          </h3>
          <p className="text-gray-500 text-sm">
            Fill out the details below to add a new sub category.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Sub Category Name */}
          <div>
            <label htmlFor="subCategoryName" className="block text-sm font-semibold text-gray-700 mb-2">
              Sub Category Name *
            </label>
            <input
              id="subCategoryName"
              type="text"
              value={formData.categoryName}
              onChange={(e) =>
                handleInputChange("categoryName", e.target.value)
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-xl 
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                         outline-none transition-all duration-200 hover:border-gray-400"
              placeholder="Enter sub category name"
              required
            />
          </div>

          {/* Slug */}
          <div>
            <label htmlFor="subCategorySlug" className="block text-sm font-semibold text-gray-700 mb-2">
              Slug Value *
            </label>
            <input
              id="subCategorySlug"
              type="text"
              value={formData.slug}
              onChange={(e) => handleInputChange("slug", e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl 
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                         outline-none transition-all duration-200 hover:border-gray-400"
              placeholder="Enter slug value"
              required
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-4 px-6 rounded-xl font-semibold text-lg 
                       transition-all duration-200 flex items-center justify-center 
                       space-x-2 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2
                       ${
                         isLoading
                           ? "bg-gray-400 cursor-not-allowed"
                           : "bg-gradient-to-r from-green-500 to-teal-500 text-white hover:from-green-600 hover:to-teal-600 hover:shadow-xl transform hover:scale-105 focus:ring-green-500"
                       }`}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Creating Sub Category...</span>
              </>
            ) : (
              <span>Add Sub Category</span>
            )}
          </button>
        </form>
      </div>

      {/* Snackbar */}
      <Snackbar
        isOpen={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
          setError(null);
        }}
        type={snackbarType}
        title={snackbarType === "error" ? "Error" : "Success"}
        message={error || apiMessage || ""}
        apiMessage={apiMessage || undefined}
        autoClose={snackbarType === "success"}
        autoCloseDelay={5000}
      />
    </div>
  );
};

export default SubCreateCategory;
